// GUIDED-FLOW (build order, increment 2): a guided flow as a focused modal or a full-screen
// panel. Left: the chapters and their steps with progress ("3 of 5 chapters"). Middle: the
// current step's fields (rendered and checked by the same engine the API runs); Save writes the
// step to its real fields (save and resume), Next and Back move, and the last stop is the Review
// with Submit. Right: the helper, which answers questions about the step and may suggest one
// value the person takes with "Use" (into the form only: the person still saves). The helper
// cannot save, submit or move; on the sealed step it sees nothing.
import {
  type Answers,
  type AnswerValue,
  checkValue,
  type FlowDef,
  fieldEditable,
  fieldVisible,
  flowDef,
  isRequired,
  reviewRows,
  type StepDef,
  stepVisible,
} from "@companyos/domain-work/flows";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogTitle,
  InfoTip,
  Input,
  Notice,
  Spinner,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleIcon,
  LockIcon,
  MessageCircleQuestionIcon,
  SendIcon,
} from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../structure/parts";
import { FilesField, FlowField, LanesCard, LinkedField } from "./flow-fields";
import {
  chaptersLine,
  type FlowHelperAnswer,
  type FlowMissing,
  type FlowRunView,
  fieldErrorsOf,
  flowKeys,
  flowQuery,
  mergedAnswers,
  stepsOf,
  submitGeneralError,
  submitIssuesOf,
  submitRefused,
  viewerOf,
  whoFills,
} from "./flow-queries";

const REVIEW = "__review";

type Props = {
  api: Api;
  company: CompanyAccess;
  runId: string;
  onClose: () => void;
};

/** The flow as a focused modal (the banner, the seat actions, a new DE). */
export function FlowModal(props: Props) {
  return (
    <Dialog open onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent
        className="h-[min(92dvh,760px)] grid-rows-[1fr] gap-0 p-0 sm:max-w-[min(1100px,calc(100%-2rem))]"
        data-slot="flow-modal"
      >
        <FlowPanel {...props} dialog />
      </DialogContent>
    </Dialog>
  );
}

/** The flow as a full-screen panel (`/c/{slug}/flows/{id}`: the invitation and chat links). */
export function FlowPage(props: Props) {
  return (
    <div className="flex h-full min-h-[calc(100dvh-8rem)] flex-col" data-slot="flow-page">
      <FlowPanel {...props} />
    </div>
  );
}

function FlowPanel({ api, company, runId, onClose, dialog = false }: Props & { dialog?: boolean }) {
  const q = useQuery(flowQuery(api, company.id, runId));
  if (q.isPending)
    return (
      <div role="status" aria-label="Loading the flow" className="flex items-center gap-2 p-6">
        <Spinner /> Loading…
      </div>
    );
  if (q.isError || !q.data)
    return (
      <div className="flex flex-col gap-3 p-6">
        {dialog ? <DialogTitle>Flow</DialogTitle> : null}
        <Notice tone="danger" role="alert" title="This flow could not be opened">
          {problemText(q.error)}
        </Notice>
        <Button variant="secondary" className="self-start" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  const def = flowDef(q.data.flow_key);
  if (!def) return null;
  return (
    <FlowBody
      api={api}
      company={company}
      view={q.data}
      def={def}
      onClose={onClose}
      dialog={dialog}
    />
  );
}

function FlowBody({
  api,
  company,
  view,
  def,
  onClose,
  dialog,
}: {
  api: Api;
  company: CompanyAccess;
  view: FlowRunView;
  def: FlowDef;
  onClose: () => void;
  dialog: boolean;
}) {
  const qc = useQueryClient();
  const co = company.id;
  const viewer = useMemo(() => viewerOf(view), [view]);
  const [draft, setDraft] = useState<Record<string, AnswerValue>>({});
  const answers: Answers = mergedAnswers(view, draft);
  const visibleSteps = stepsOf(def).filter((s) => stepVisible(s, viewer, answers));
  const [at, setAt] = useState<string>(() => {
    const firstOpen = visibleSteps.find((s) => !view.saved_steps.includes(s.key));
    return firstOpen?.key ?? visibleSteps[0]?.key ?? REVIEW;
  });
  const step = visibleSteps.find((s) => s.key === at) ?? null;
  const index = step ? visibleSteps.indexOf(step) : visibleSteps.length;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [missing, setMissing] = useState<FlowMissing[] | null>(null);
  /** Moving to the Review with unsaved typing: Save or Discard first. */
  const [leaving, setLeaving] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  /** Labels of unsaved changes dropped because the field can no longer be written. */
  const [dropped, setDropped] = useState<string[]>([]);
  /** Where the move that was held for the dropped-changes warning goes on its OK. */
  const [afterDropped, setAfterDropped] = useState<string | null>(null);
  const open = view.status === "open";
  const unsaved = Object.keys(draft).length > 0;
  /** The dropped-changes warning is up: nothing moves or saves until its OK. */
  const holding = dropped.length > 0;

  const put = (fresh: FlowRunView) => {
    qc.setQueryData(flowKeys.run(co, view.id), fresh);
    void qc.invalidateQueries({ queryKey: flowKeys.mineOpen(co) });
    void qc.invalidateQueries({ queryKey: ["company", co, "flows", "seat-open"] });
    // COLLECT-FLOWS: the progress view counts the answers just saved.
    void qc.invalidateQueries({ queryKey: ["company", co, "flows", "collection"] });
  };

  const withdraw = useMutation({
    mutationFn: () => api.client.flows.cancel({ params: { co, id: view.id } }),
    onSuccess: () => {
      setWithdrawing(false);
      void qc.invalidateQueries({ queryKey: flowKeys.all(co) });
      toast.add({ title: `${def.title} withdrawn`, type: "success" });
    },
  });

  /** The step's values to send: what was typed, and template suggestions shown but not saved. */
  const valuesOf = (s: StepDef): Record<string, AnswerValue> => {
    const out: Record<string, AnswerValue> = {};
    for (const f of s.fields) {
      if (f.type === "linked" || !fieldVisible(f, viewer, answers) || !fieldEditable(f, viewer))
        continue;
      if (f.key === "access_level" && view.access_level_locked) continue;
      if (f.key in draft) out[f.key] = draft[f.key] ?? null;
      else if (view.prefill[f.key] !== undefined && view.answers[f.key] == null)
        out[f.key] = view.prefill[f.key] ?? null;
    }
    return out;
  };

  const save = useMutation({
    mutationFn: (input: {
      step: StepDef;
      values: Record<string, AnswerValue>;
      /** The drafts as they were when the save was sent. */
      sent: Record<string, AnswerValue>;
    }) =>
      api.client.flows.saveStep({
        params: { co, id: view.id, step: input.step.key },
        body: { values: input.values },
      }),
    onSuccess: (fresh, input) => {
      put(fresh);
      // Only drafts still equal to what was sent are cleared: typing since then stays.
      setDraft((d) => {
        const next = { ...d };
        for (const [k, v] of Object.entries(input.sent))
          if (k in next && sameValue(next[k], v)) delete next[k];
        return next;
      });
      setErrors({});
    },
    onError: (e, input) => setErrors(fieldErrorsOf(e, input.step)),
  });

  /**
   * Checks the step's values with the engine, then saves. Resolves whether it saved and the
   * labels of unsaved changes it dropped (the caller shows them and holds any navigation).
   */
  async function saveStep(
    s: StepDef,
    extra: Record<string, AnswerValue> = {},
  ): Promise<{ ok: boolean; dropped: string[] }> {
    const values = { ...valuesOf(s), ...extra };
    // Only drafts actually sent are cleared after the save; drafts of this step's fields that are
    // no longer sendable (hidden, not yours, locked) are dropped, and the person is told.
    const sent = Object.fromEntries(Object.entries(draft).filter(([k]) => k in values));
    const unsendable = s.fields.filter((f) => f.key in draft && !(f.key in values));
    const droppedNow = unsendable.map((f) => f.label);
    if (unsendable.length)
      setDraft((d) => {
        const next = { ...d };
        for (const f of unsendable) delete next[f.key];
        return next;
      });
    const bad: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) {
      const f = s.fields.find((x) => x.key === k);
      if (!f) continue;
      const checked = checkValue(f, v);
      if (!checked.ok) bad[k] = checked.message;
      else values[k] = checked.value;
    }
    if (Object.keys(bad).length) {
      setErrors(bad);
      return { ok: false, dropped: droppedNow };
    }
    try {
      await save.mutateAsync({ step: s, values, sent });
      return { ok: true, dropped: droppedNow };
    } catch {
      return { ok: false, dropped: droppedNow };
    }
  }

  /**
   * Saves every step that has unsaved typing (the helper may fill a field of another step),
   * collecting what was dropped across all of them; it stops on the first step that fails, or
   * the last one that dropped something, so the person sees it there.
   */
  async function saveAll(): Promise<{ ok: boolean; dropped: string[]; step: string | null }> {
    const all: string[] = [];
    let where: string | null = null;
    for (const s of stepsOf(def))
      if (s.fields.some((f) => f.key in draft)) {
        setAt(s.key);
        const r = await saveStep(s);
        if (r.dropped.length) {
          all.push(...r.dropped);
          where = s.key;
        }
        if (!r.ok) return { ok: false, dropped: all, step: s.key };
      }
    return { ok: true, dropped: all, step: where };
  }

  /** After a save: with nothing dropped, go on; else stay, show it, and go on once acknowledged. */
  const settle = (r: { dropped: string[] }, then: string | null) => {
    if (r.dropped.length) {
      setDropped(r.dropped);
      setAfterDropped(then);
    } else if (then) go(then);
    else setDropped([]);
  };

  /** To the Review: with unsaved typing, ask first. */
  const toReview = () => {
    if (unsaved || save.isPending) setLeaving(true);
    else go(REVIEW);
  };

  const submit = useMutation({
    mutationFn: () => api.client.flows.submit({ params: { co, id: view.id } }),
    onSuccess: (fresh) => {
      put(fresh);
      setMissing(null);
      // DE onboarding's submit also submits the DE's job: its cached reads are stale.
      if (view.de_membership_id)
        void qc.invalidateQueries({ queryKey: ["company", co, "de", view.de_membership_id] });
      toast.add({ title: `${def.title} submitted`, type: "success" });
    },
    // Refused for missing fields: the run is read again and its `missing` list shown.
    onError: async (e) => {
      if (!submitRefused(e)) return;
      setMissing(view.missing);
      try {
        const fresh = await api.client.flows.get({ params: { co, id: view.id } });
        qc.setQueryData(flowKeys.run(co, view.id), fresh);
        setMissing(fresh.missing);
      } catch {
        // The list as last read stays.
      }
    },
  });

  const go = (key: string) => {
    setErrors({});
    setDropped([]);
    setAfterDropped(null);
    setAt(key);
  };
  const next = visibleSteps[index + 1]?.key ?? REVIEW;
  const rendered = step ? step.fields.filter((f) => fieldVisible(f, viewer, answers)) : [];
  /** Errors no field on screen shows (a `step` refusal, a field of another step): the summary. */
  const unmatched = Object.entries(errors).filter(
    ([k]) => !rendered.some((f) => f.key === k && f.type !== "linked" && f.type !== "confirm"),
  );
  const prev = at === REVIEW ? visibleSteps.at(-1)?.key : visibleSteps[index - 1]?.key;

  const rail = (
    <nav
      aria-label="Chapters"
      data-slot="flow-rail"
      className="flex flex-col gap-3 border-border p-4 md:w-60 md:shrink-0 md:border-r max-md:border-b"
    >
      <div className="flex flex-col gap-0.5">
        <span className="text-xs text-muted-foreground" data-slot="flow-chapters">
          {chaptersLine(view)}
        </span>
        <span className="truncate font-medium">{view.seat_title}</span>
      </div>
      <ol className="flex flex-col gap-2">
        {def.chapters.map((c) => {
          const steps = c.steps.filter((s) => visibleSteps.includes(s));
          if (!steps.length) return null;
          // Completion is the server's (it sees every answer; this view of them is cut).
          const cp = view.chapters.find((x) => x.key === c.key);
          return (
            <li key={c.key} data-chapter={c.key} data-complete={cp?.complete ? true : undefined}>
              <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                {cp?.complete ? (
                  <CheckIcon aria-label="Done" className="size-3.5 text-success-text" />
                ) : (
                  <CircleIcon aria-hidden="true" className="size-3" />
                )}
                {c.title}
              </span>
              <ul className="mt-1 flex flex-col">
                {steps.map((s) => {
                  const done = cp?.steps.find((x) => x.key === s.key)?.complete;
                  return (
                    <li key={s.key}>
                      <button
                        type="button"
                        disabled={holding}
                        onClick={() => go(s.key)}
                        aria-current={at === s.key ? "step" : undefined}
                        className={cn(
                          "flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-sm hover:bg-muted",
                          at === s.key && "bg-muted font-medium",
                        )}
                      >
                        {s.secure ? <LockIcon aria-label="Sealed" className="size-3" /> : null}
                        <span className="min-w-0 flex-1 truncate">{s.title}</span>
                        {done ? <CheckIcon aria-label="Complete" className="size-3" /> : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
        <li>
          <button
            type="button"
            disabled={holding}
            onClick={toReview}
            aria-current={at === REVIEW ? "step" : undefined}
            className={cn(
              "flex w-full items-center rounded-md px-2 py-1 text-left text-sm hover:bg-muted",
              at === REVIEW && "bg-muted font-medium",
            )}
          >
            Review
          </button>
        </li>
      </ol>
    </nav>
  );

  const title = (
    <span className="flex items-center gap-1.5">
      {dialog ? (
        <DialogTitle className="pr-0">{def.title}</DialogTitle>
      ) : (
        <h1 className="text-lg font-semibold">{def.title}</h1>
      )}
      {view.status !== "open" ? (
        <Badge variant={view.status === "completed" ? "success" : "neutral"}>
          {view.status === "completed" ? "Submitted" : "Withdrawn"}
        </Badge>
      ) : null}
      <span className="flex-1" />
      {open && view.viewer.can_cancel ? (
        <Button
          variant="ghost"
          size="xs"
          className={cn("hover:text-danger-text", dialog && "mr-8")}
          data-slot="flow-withdraw"
          onClick={() => {
            withdraw.reset();
            setWithdrawing(true);
          }}
        >
          Withdraw
        </Button>
      ) : null}
    </span>
  );

  const dialogs = (
    <>
      <AlertDialog
        open={withdrawing}
        onOpenChange={(o) => !withdraw.isPending && setWithdrawing(o)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw this {def.title.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>
              {view.assignee_name ?? "The person"} can no longer fill it. Answers already saved stay
              on the seat.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {withdraw.error ? (
            <Notice tone="danger" role="alert" title="Withdrawing failed">
              {problemText(withdraw.error)}
            </Notice>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={withdraw.isPending}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={withdraw.isPending}
              onClick={() => withdraw.mutate()}
            >
              {withdraw.isPending ? "Withdrawing…" : "Withdraw"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={leaving} onOpenChange={(o) => !save.isPending && setLeaving(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              Save them before the review, or discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={save.isPending}>Cancel</AlertDialogCancel>
            <Button
              variant="secondary"
              disabled={save.isPending}
              onClick={() => {
                setDraft({});
                setErrors({});
                setLeaving(false);
                go(REVIEW);
              }}
            >
              Discard
            </Button>
            <Button
              disabled={save.isPending}
              onClick={async () => {
                const r = await saveAll();
                setLeaving(false);
                if (!r.ok) {
                  if (r.dropped.length) settle(r, null);
                  return;
                }
                // Something dropped: stay on that step with the warning; OK goes to the Review.
                if (r.step && r.dropped.length) setAt(r.step);
                settle(r, REVIEW);
              }}
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  const main = (
    <section
      className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-5"
      data-slot="flow-main"
    >
      {title}
      {at === REVIEW || !step ? (
        <Review
          def={def}
          view={view}
          open={open}
          missing={missing}
          invalid={submitIssuesOf(submit.error)}
          pending={submit.isPending}
          blocked={unsaved || save.isPending ? "Save or discard your changes first." : null}
          onEdit={go}
          onSubmit={() => submit.mutate()}
          onClose={onClose}
          error={submitGeneralError(submit.error) ? submit.error : null}
        />
      ) : (
        <form
          className="flex flex-col gap-4"
          data-slot="flow-step"
          data-step={step.key}
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (holding) return;
            void saveStep(step).then((r) => settle(r, null));
          }}
        >
          <span className="flex items-center gap-1.5">
            <h2 className="text-base font-medium">{step.title}</h2>
            <InfoTip label={`About ${step.title.toLowerCase()}`}>{step.info}</InfoTip>
          </span>
          {dropped.length ? (
            <Notice tone="warning" role="alert" data-slot="flow-dropped">
              <span className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1">
                  Not saved, no longer yours to change: {dropped.join(", ")}
                </span>
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  onClick={() => {
                    const then = afterDropped;
                    setAfterDropped(null);
                    // To the Review through its own check (typing left on other steps asks).
                    if (then === REVIEW) {
                      setDropped([]);
                      toReview();
                    } else if (then) go(then);
                    else setDropped([]);
                  }}
                >
                  {afterDropped ? "OK, continue" : "OK"}
                </Button>
              </span>
            </Notice>
          ) : null}
          {save.error || unmatched.length ? (
            <Notice tone="danger" role="alert" title="Saving failed" data-slot="flow-save-error">
              {unmatched.length ? (
                <ul className="flex flex-col gap-0.5">
                  {unmatched.map(([k, m]) => (
                    <li key={k}>{m}</li>
                  ))}
                </ul>
              ) : (
                problemText(save.error)
              )}
            </Notice>
          ) : null}
          {rendered.map((f) => {
            const editable =
              open &&
              fieldEditable(f, viewer) &&
              !(f.key === "access_level" && view.access_level_locked);
            if (f.type === "linked")
              return (
                <LinkedField
                  key={f.key}
                  api={api}
                  company={company}
                  view={view}
                  field={f}
                  editable={editable}
                />
              );
            if (f.type === "files")
              return (
                <FilesField
                  key={f.key}
                  api={api}
                  co={co}
                  field={f}
                  required={isRequired(f, answers)}
                  value={answers[f.key]}
                  known={view.files}
                  editable={editable}
                  error={errors[f.key]}
                  onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
                />
              );
            if (f.type === "confirm")
              return (
                <LanesCard
                  key={f.key}
                  field={f}
                  view={view}
                  confirmed={view.answers[f.key] === true}
                  editable={editable}
                  pending={save.isPending || holding}
                  onConfirm={() =>
                    void saveStep(step, { [f.key]: true }).then((r) => settle(r, null))
                  }
                />
              );
            return (
              <FlowField
                key={f.key}
                field={f}
                required={isRequired(f, answers)}
                value={answers[f.key]}
                saved={view.answers[f.key]}
                editable={editable}
                sealed={Boolean(step.secure)}
                error={errors[f.key]}
                options={view.options}
                onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
              />
            );
          })}
          <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              disabled={!prev || holding}
              onClick={() => prev && go(prev)}
            >
              <ChevronLeftIcon data-icon="inline-start" aria-hidden="true" />
              Back
            </Button>
            <span className="flex-1" />
            {open ? (
              <Button type="submit" variant="secondary" disabled={save.isPending || holding}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            ) : null}
            <Button
              type="button"
              disabled={save.isPending || holding}
              onClick={async () => {
                if (holding) return;
                const r = open ? await saveStep(step) : { ok: true, dropped: [] };
                if (!r.ok) {
                  if (r.dropped.length) settle(r, null);
                  return;
                }
                // Typing left on other steps (a helper suggestion) still asks first.
                const elsewhere = Object.keys(draft).some(
                  (k) => !step.fields.some((f) => f.key === k),
                );
                if (next === REVIEW && elsewhere && !r.dropped.length) setLeaving(true);
                else settle(r, next);
              }}
            >
              {next === REVIEW ? "Review" : "Next"}
              <ChevronRightIcon data-icon="inline-end" aria-hidden="true" />
            </Button>
          </div>
        </form>
      )}
    </section>
  );

  return (
    <div className="flex h-full min-h-0 flex-col md:flex-row" data-slot="flow-panel">
      {dialogs}
      {rail}
      {main}
      <Helper
        api={api}
        co={co}
        runId={view.id}
        step={at === REVIEW ? null : step}
        onUse={(field, value) => {
          // While the dropped-changes warning is up, a suggestion neither moves nor fills.
          if (holding) return;
          const target = stepsOf(def).find((s) => s.fields.some((f) => f.key === field));
          if (target && target.key !== at) setAt(target.key);
          setDraft((d) => ({ ...d, [field]: value }));
        }}
      />
    </div>
  );
}

const sameValue = (a: AnswerValue | undefined, b: AnswerValue | undefined) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const display = (v: AnswerValue, type: string): string => {
  if (v === null) return "—";
  if (type === "linked") return typeof v === "number" ? (v ? `${v} in place` : "None yet") : "—";
  if (type === "secret" || v === true) return type === "boolean" ? "Yes" : "Filled";
  if (v === false) return "No";
  if (type === "files" && Array.isArray(v)) return `${v.length} file${v.length === 1 ? "" : "s"}`;
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
};

function Review({
  def,
  view,
  open,
  missing,
  invalid,
  pending,
  blocked,
  error,
  onEdit,
  onSubmit,
  onClose,
}: {
  def: FlowDef;
  view: FlowRunView;
  open: boolean;
  missing: FlowMissing[] | null;
  /** A refused submit's errors other than missing required fields. */
  invalid: string[];
  pending: boolean;
  /** Why Submit is off (unsaved typing, a save in flight), else null. */
  blocked: string | null;
  error: unknown;
  onEdit: (step: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const viewer = viewerOf(view);
  const rows = reviewRows(def, view.answers, viewer);
  const fieldOf = (key: string) =>
    def.chapters
      .flatMap((c) => c.steps)
      .flatMap((s) => s.fields)
      .find((x) => x.key === key);
  const labelOf = (row: (typeof rows)[number]) => {
    const v = row.value;
    const opts =
      row.type === "seat" ? view.options.seats : row.type === "role" ? view.options.roles : null;
    if (opts && typeof v === "string") return opts.find((o) => o.id === v)?.label ?? v;
    const f = fieldOf(row.field);
    if (f?.bind.kind === "secure") return v === null ? "—" : "Filled";
    if (f?.options && typeof v === "string")
      return f.options.find((o) => o.value === v)?.label ?? v;
    if (f?.options && Array.isArray(v))
      return v.map((x) => f.options?.find((o) => o.value === x)?.label ?? x).join(", ");
    return display(v, row.type);
  };
  return (
    <div className="flex flex-col gap-4" data-slot="flow-review">
      <h2 className="text-base font-medium">Review</h2>
      {def.chapters.map((c) => {
        const mine = rows.filter((r) => r.chapter === c.key);
        if (!mine.length) return null;
        return (
          <section key={c.key} className="flex flex-col gap-1">
            <h3 className="text-xs font-medium text-muted-foreground">{c.title}</h3>
            <dl className="grid grid-cols-[minmax(8rem,auto)_1fr_auto] items-center gap-x-3 gap-y-1">
              {mine.map((r) => (
                <div key={r.field} className="contents" data-review={r.field}>
                  <dt className="text-sm text-muted-foreground">
                    {r.label}
                    {(() => {
                      const f = fieldOf(r.field);
                      return f && isRequired(f, view.answers) ? (
                        <span className="text-faint"> *</span>
                      ) : null;
                    })()}
                  </dt>
                  <dd className={cn("min-w-0 truncate text-sm", !r.answered && "text-faint")}>
                    {labelOf(r)}
                  </dd>
                  <dd>
                    {r.editable && open ? (
                      <Button variant="ghost" size="xs" onClick={() => onEdit(r.step)}>
                        Edit
                      </Button>
                    ) : null}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        );
      })}
      {invalid.length ? (
        <Notice tone="danger" role="alert" title="Can't submit yet" data-slot="flow-invalid">
          <ul className="flex flex-col gap-0.5">
            {invalid.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {missing?.length ? (
        <Notice tone="warning" role="alert" title="Still missing" data-slot="flow-missing">
          <ul className="flex flex-col gap-0.5">
            {missing.map((m) => (
              <li key={`${m.step}-${m.field}`}>
                {m.label} · {whoFills(m.fill, viewer.audiences)}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {error ? (
        <Notice tone="danger" role="alert" title="Submitting failed">
          {problemText(error)}
        </Notice>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {open ? (
          <Button onClick={onSubmit} disabled={pending || blocked !== null}>
            <SendIcon data-icon="inline-start" aria-hidden="true" />
            {pending ? "Submitting…" : "Submit"}
          </Button>
        ) : null}
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {open && blocked ? (
          <span className="text-xs text-muted-foreground" data-slot="submit-blocked">
            {blocked}
          </span>
        ) : null}
      </div>
    </div>
  );
}

type Exchange = { question: string; answer: FlowHelperAnswer | null; error?: string };

/** The side helper: questions about the step; a suggestion is taken into the form only. */
function Helper({
  api,
  co,
  runId,
  step,
  onUse,
}: {
  api: Api;
  co: string;
  runId: string;
  step: StepDef | null;
  onUse: (field: string, value: AnswerValue) => void;
}) {
  const [question, setQuestion] = useState("");
  const [log, setLog] = useState<Exchange[]>([]);
  const ask = useMutation({
    mutationFn: (input: { step: string; question: string }) =>
      api.client.flows.helper({
        params: { co, id: runId },
        body: { step_key: input.step, question: input.question },
      }),
  });
  async function send(e: FormEvent) {
    e.preventDefault();
    const text = question.trim();
    if (!text || !step) return;
    setQuestion("");
    try {
      const answer = await ask.mutateAsync({ step: step.key, question: text });
      setLog((l) => [...l, { question: text, answer }]);
    } catch (err) {
      setLog((l) => [...l, { question: text, answer: null, error: problemText(err) }]);
    }
  }
  return (
    <aside
      aria-label="Helper"
      data-slot="flow-helper"
      className="flex flex-col gap-3 border-border p-4 md:w-72 md:shrink-0 md:border-l max-md:border-t"
    >
      <span className="flex items-center gap-1.5 text-sm font-medium">
        <MessageCircleQuestionIcon aria-hidden="true" className="size-4" />
        Helper
        <InfoTip label="About the helper">
          Ask what a field is for or how to fill it. It can suggest a value; it never saves.
        </InfoTip>
      </span>
      {step?.secure ? (
        <p
          className="flex items-center gap-1.5 text-xs text-muted-foreground"
          data-slot="helper-sealed"
        >
          <LockIcon aria-hidden="true" className="size-3" />
          The helper can't see these values.
        </p>
      ) : null}
      <ol className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto text-sm">
        {log.map((x, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: the log only grows
          <li key={i} className="flex flex-col gap-1" data-slot="helper-exchange">
            <span className="self-end rounded-lg bg-muted px-2 py-1">{x.question}</span>
            {x.answer ? (
              <span className="text-secondary" data-slot="helper-answer">
                {x.answer.answer}
              </span>
            ) : (
              <span className="text-danger-text">{x.error}</span>
            )}
            {x.answer?.suggestion ? (
              <span
                className="flex items-center gap-2 rounded-md border border-border px-2 py-1"
                data-slot="helper-suggestion"
              >
                <span className="min-w-0 flex-1 truncate">
                  {x.answer.suggestion.label}:{" "}
                  <span className="font-medium">
                    {Array.isArray(x.answer.suggestion.value)
                      ? x.answer.suggestion.value.join(", ")
                      : String(x.answer.suggestion.value)}
                  </span>
                </span>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => {
                    const s = x.answer?.suggestion;
                    if (s) onUse(s.field, s.value as AnswerValue);
                  }}
                >
                  Use
                </Button>
              </span>
            ) : null}
          </li>
        ))}
      </ol>
      <form className="flex gap-2" onSubmit={(e) => void send(e)}>
        <Input
          aria-label="Ask the helper"
          placeholder={step ? "Ask about this step" : "Open a step to ask"}
          value={question}
          maxLength={1000}
          disabled={!step}
          onChange={(e) => setQuestion(e.target.value)}
        />
        <Button
          type="submit"
          variant="secondary"
          disabled={!step || !question.trim() || ask.isPending}
        >
          Ask
        </Button>
      </form>
    </aside>
  );
}
